// ============================================================================
// DONNÉES DES PROJETS - données combinées depuis projectsDetailData.ts
// ============================================================================

import marinetteLogo from '../assets/marinette-logo.png';
import borrowedTimeLogo from '../assets/borrowed-time-logo.png';
import lolLogo from '../assets/lol-logo.png';
import moustikLogoWhite from '../assets/moustik-logo-white.png';

export interface VisualAsset {
  src: string;
  alt?: string;
  type?: 'image' | 'video';
  gridColumn?: string;
  gridRow?: string;
  objectFit?: 'cover' | 'contain';
  objectPosition?: string;
  aspectRatio?: string;
}

export interface ProjectDetail {
  id: string;
  title: string;
  subtitle: string;
  sections: {
    title: string;
    content: string[];
  }[];
  gallery?: VisualAsset;
  theme?: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    frameColor: string;
    fontFamily: string;
    titleFont: string;
  };
  heroImage?: string;
  heroLogo?: string;
  logoConfig?: {
    maxWidth?: string;
    maxHeight?: string;
    fit?: 'contain' | 'cover';
    padding?: string;
    backgroundColor?: string;
  };
  heroBadge?: string;
  heroTagline?: string;
  sectionVisuals?: (string[] | undefined)[];
  lookbook?: VisualAsset;
  images?: string[];
  objectiveRightImage?: string;
  storytellingRightImage?: string;
  moodboardAfterImage?: string;
  dessinsAfterImage?: string;
  galleryImages?: string[];
  silhouettes?: {
    rows?: string[];
    items?: string[];
  };
}

export const projectsData: Record<string, ProjectDetail> = {
  'marinette': {
    id: "marinette",
    title: "Marinette",
    subtitle: 'Collection Maritime • Automne-Hiver',
    sections: [
      {
        title: 'Notre concept de marque',
        content: [
          'Nous utilisons des matériaux qui sont destinés à être jetés, ils proviennent directement des bateaux comme la corde, les voiles ou le filet de pêche.',
          'Ce sont des matériaux qui ne sont pas beaucoup mis en avant et auxquelles on ne pense pas forcément à utiliser dans le milieu de l\'habillement. Et quoi de mieux pour représenter notre univers maritime que d\'utiliser des produits qui viennent directement de cet univers.',
          'Nous avons déjà eu l\'occasion de créer une collection qui avait eu du succès.'
        ]
      },
      {
        title: 'Notre concept de collection',
        content: [
          'Notre collection est un véritable hommage aux femmes, plus précisément notre but est de mettre en avant les professions où les femmes excellent et sont bien trop souvent oubliées. Ici dans cette collection capsule nous avons voulu rendre hommage aux femmes qui travaillent et dirigent des bateaux, des « Marinette ». Petit clin d\'œil au nom de notre marque.',
          'Elle est basée sur l\'histoire d\'une Marinette partie en mer pendant plusieurs mois. Qui à force d\'être sur ce bateau a finit par se créer de nouveaux vêtements en s\'inspirant des nœuds maritimes, en créant des accessoires en corde, avec des morceaux de voile.',
          'Nous produisons un certain nombre par pièce donc on part sur une série limitée mais si jamais la demande est plus forte nous produisons à la demande. Un peu comme Veja.'
        ]
      },
      {
        title: 'Le design de la collection',
        content: [
          'Pour le design de cette collection nous avons pris comme axe les bateaux de pêche, les voiliers et les bateaux gonflables.',
          'On retrouve des éléments distinctifs de chaque bateau dans les pièces de la collection, avec la couleur orange et les « boudins » des bateaux gonflables, le filet de pêche du bateau de pêche et la corde et la voile du voilier. Nous avons aussi repris la forme de la coque qui est commune aux 3 bateaux.',
          'La collection est plutôt streetwear avec des pièces plus chics, des pièces pour aller en soirée ou pour tous les jours.',
          'C\'est une collection pour la saison automne-hiver, avec de la maille, une doudoune, des pantalons…'
        ]
      }
    ],
    theme: {
      primaryColor: '#0F1B3D',
      secondaryColor: '#CDA873',
      accentColor: '#E8ECF5',
      backgroundColor: '#FDFCFA',
      frameColor: '#B8A890',
      fontFamily: "'Georgia', 'Garamond', serif",
      titleFont: "'Georgia', serif"
    },
    heroLogo: marinetteLogo,
    logoConfig: {
      maxWidth: '65vw',
      maxHeight: '70vh',
      fit: 'contain',
      padding: '50px',
      backgroundColor: '#FDFCFA'
    },
    heroBadge: 'Capsule Marine',
    heroTagline: 'Upcycling inspiré des femmes de la mer',
    sectionVisuals: [
      ['/assets/marinette/visuals/marinette-concept-marque.png'],
      ['/assets/marinette/visuals/marinette-concept-collection.jpg'],
      ['/assets/marinette/visuals/marinette-moodboard.png'],
      []
    ],
    lookbook: { src: '/projects/marinette/Lookbook/dessins.png', alt: 'Marinette dessins' },
    images: [
      '/assets/date.png'
    ]
  },
  'borrowed-time': {
    id: 'borrowed-time',
    title: 'Borrowed Time',
    subtitle: 'Défilé Engagé • Liberté & Identité',
    sections: [
      {
        title: 'Objectif',
        content: [
          'Nous sommes quatre étudiantes et nous avons organisé un défilé qui reflète notre vision et notre savoir-faire. Cet événement a pour but de mettre en avant notre créativité tout en transmettant un message engagé sur la répression des femmes et la lutte pour la liberté individuelle.',
          'À travers des tenues innovantes, à la fois artistiques et accessibles, nous dénonçons les inégalités et affirmons que la mode peut être un moyen d\'expression et de revendication. Dans un contexte mondial marqué par les luttes pour les droits des femmes, notre défilé s\'inscrit comme une prise de position actuelle et engagée.'
        ]
      },
      {
        title: 'Story telling',
        content: [
          'L\'histoire suit une jeune femme du XIXᵉ siècle, élevée dans une famille bourgeoise stricte où elle doit obéir aux règles et aux attentes sociales. Fatiguée d\'être contrôlée, elle se révolte contre ce destin imposé et finit par fuir, cherchant sa liberté.',
          'Propulsée dans les années 2000, elle découvre un monde moderne où les gens vivent sans contraintes et où la mode, notamment le streetwear, devient pour elle un moyen d\'expression et d\'affirmation de soi. À travers cette nouvelle culture, elle apprend à se connaître et à s\'émanciper de toutes les normes.',
          'Son parcours la mène à une redéfinition profonde de son identité : elle rejette les rôles de genre traditionnels et se reconnaît comme non genrée, symbole ultime de sa libération et de son indépendance.'
        ]
      }
    ],
    gallery: { src: '/assets/borrowed-time/gallery-01.png', alt: 'Borrowed Time Galerie', type: 'image' },
    theme: {
      primaryColor: '#C74B7C',
      secondaryColor: '#2A1F1F',
      accentColor: '#F8EFE8',
      backgroundColor: '#FFFBF7',
      frameColor: '#D4A574',
      fontFamily: "'Crimson Text', serif",
      titleFont: "'Dancing Script', cursive"
    },
    heroLogo: borrowedTimeLogo,
    logoConfig: {
      maxWidth: '60vw',
      maxHeight: '70vh',
      fit: 'contain',
      padding: '40px',
      backgroundColor: '#FFFBF7'
    },
    heroBadge: 'Défilé narratif',
    heroTagline: 'Un voyage du XIXᵉ siècle au streetwear contemporain',
    sectionVisuals: [
  ['/assets/borrowed-time/objectif.jpg'],
  ['/assets/borrowed-time/borrowed-time-canva.jpg']
    ],
    lookbook: { src: '/assets/borrowed-time/moodboard.png', alt: 'Borrowed Time Moodboard' },
    moodboardAfterImage: '/assets/borrowed-time/dessin.png',
    images: [
      '/assets/borrowed-time/date (3).png',
    ]
  },
  'chemistry-off-white-league-of-legends': {
    id: 'chemistry-off-white-league-of-legends',
    title: 'CHEMISTRY OFF-WHITE X LEAGUE OF LEGENDS',
    subtitle: 'Collaboration • Mode & Gaming',
    sections: [
      {
        title: 'LE STORYTELLING',
        content: [
          'La collaboration entre Off-White et League of Legends naît de la rencontre entre deux univers différents mais complémentaires : l\'esthétique urbaine et innovante de la marque et le monde fantastique et compétitif du jeu. Ensemble, ils créent une collection qui fusionne les codes d\'Off-White avec les symboles emblématiques de LoL.',
          'L\'alliance ne se limite pas aux vêtements : elle s\'étend aussi dans le jeu avec des cosmétiques exclusifs et une carte revisitée dans l\'esprit de la marque, offrant aux joueurs une expérience unique. Ce partenariat illustre parfaitement la créativité et l\'audace à l\'intersection de la mode, du divertissement et de la culture populaire.'
        ]
      },
      {
        title: 'LES PERSONNAGES',
        content: [
          'Nous avons pris K\'Sante car il correspondait bien avec le style de Off White. De nature efféminée il sort des clichés des jeux vidéos.',
          'Nous avons choisi Riven, car c\'est un personnage qui n\'est pas sexualisé, ses skins favorisent plutôt l\'esprit pratique qu\'esthétique.',
          'Les skins sont surtout inspirés du jeu League of Legends avec des éléments reconnaissables de Off-White.',
          'Pour le design, c\'est une réinterprétation du skin Prestige Empyrean.'
        ]
      },
      {
        title: 'LE DESIGN DE LA COLLECTION',
        content: [
          'Pour le design, nous avons mélangé l\'univers futuriste et élégant de League of Legends avec l\'esthétique streetwear signature d\'Off-White.',
        ]
      },
      {
        title: 'MOOD BOARD',
        content: [
          'Notre planche d\'inspiration capture l\'essence de cette collaboration unique, mêlant les codes visuels d\'Off-White et l\'univers épique de League of Legends.'
        ]
      },
      {
        title: 'TENUE EN VENTE',
        content: [
          'Tenue plutôt streetwear, qui est un thème beaucoup utilisé dans les collections mises en vente par Off White. On retrouve sur les vêtements des éléments reconnaissables du jeu, comme du vocabulaire utilisé par les joueurs et des graphismes du jeu.'
        ]
      }
    ],
    gallery: { src: '/assets/chemistry-off-white-league-of-legends/Galerie/01.jpg', alt: 'Off White X League Of Legends Galerie' },
    theme: {
      primaryColor: '#D4002A',
      secondaryColor: '#1C1C1C',
      accentColor: '#FFF5F2',
      backgroundColor: '#FCFCFC',
      frameColor: '#000000',
      fontFamily: "'Roboto', sans-serif",
      titleFont: "'Bebas Neue', sans-serif"
    },
    heroLogo: lolLogo,
    logoConfig: {
      maxWidth: '70vw',
      maxHeight: '75vh',
      fit: 'contain',
      padding: '55px',
      backgroundColor: '#FCFCFC'
    },
    heroBadge: 'Collaboration Capsule 2024',
    heroTagline: "Quand l'avant-garde Off-White rencontre l'énergie de League of Legends.",
    sectionVisuals: [
      ['/assets/chemistry-off-white-league-of-legends/visuals/lol-story.png'],
      ['/assets/chemistry-off-white-league-of-legends/visuals/lol-character1.png', '/assets/chemistry-off-white-league-of-legends/visuals/lol-character2.png'],
      ['/assets/chemistry-off-white-league-of-legends/visuals/lol-design1.jpg', '/assets/chemistry-off-white-league-of-legends/visuals/lol-design2.jpg'],
      [],
      ['/assets/chemistry-off-white-league-of-legends/visuals/lol-outfit.png']
    ],
    lookbook: { src: '/projects/chemistry-off-white-league-of-legends/Moodboard/moodboard.png', alt: 'Off White X League Of Legends Moodboard' },
    images: [
      '/assets/chemistry-off-white-league-of-legends/Galerie/01.jpg',
      '/assets/chemistry-off-white-league-of-legends/Galerie/02.jpg',
      '/assets/chemistry-off-white-league-of-legends/Galerie/03.jpg'
    ]
  },
  'moustik': {
    id: 'moustik',
    title: 'Moustik',
    subtitle: 'Haute Couture • Automne/Hiver 2025',
    sections: [
      {
        title: 'Storytelling',
        content: [
          'En 2024 naît Moustik, une marque française audacieuse et élégante, pensée pour transcender les codes ordinaires de la mode. Inspirée par l\'avant-garde, notamment par Schiaparelli, Moustik ne suit pas les tendances : elle les crée et les défie.',
          'Ses pièces flamboyantes, aux textures raffinées, matières luxueuses et couleurs vibrantes, racontent chacune leur propre histoire. Plus qu\'une marque de vêtements, Moustik est une expression de soi. Chaque détail est conçu pour révéler la personnalité de celle ou celui qui la porte, célébrant l\'individualité et la diversité sous toutes ses formes.',
          'Moustik aspire à incarner la mode de demain : un espace où l\'audace rencontre la sophistication, et où s\'habiller devient un acte créatif, une véritable forme d\'art.'
        ]
      },
      {
        title: 'Notre collection',
        content: [
          'Pour sa première collection, Moustik voit grand. La marque explore les volumes, les structures et les matières originales, tout en les associant à des coupes classiques. Inspirée par la vie quotidienne, elle observe et écoute celles et ceux qui l\'entourent pour créer des silhouettes qui reflètent leurs histoires et leur singularité.',
          'La couleur phare de cette collection Automne/Hiver 2025 est le jaune : une teinte vive, énergique et optimiste, véritable signature visuelle. Elle est complétée par des touches de violet, symbole de transformation et de noblesse, du gris pour l\'équilibre, et du blanc pour le renouveau.',
          'Cette collection Haute Couture met avant tout la personne au centre. Elle révèle, célèbre et exalte l\'identité de chacun. Moustik affirme sa présence : nous sommes là — visibles, audacieux, inoubliables.'
        ]
      }
    ],
    gallery: { src: '/assets/moustik/Galerie/DSC03331.jpg', alt: 'Moustik Galerie' },
    theme: {
      primaryColor: '#F5FF00',
      secondaryColor: '#E0E5E3',
      accentColor: '#2A2A2A',
      backgroundColor: '#0D0D0D',
      frameColor: '#F5FF00',
      fontFamily: "'Helvetica Neue', 'Arial', sans-serif",
      titleFont: "'Bebas Neue', sans-serif"
    },
    heroLogo: moustikLogoWhite,
    logoConfig: {
      maxWidth: '75vw',
      maxHeight: '75vh',
      fit: 'contain',
      padding: '60px 50px',
      backgroundColor: '#0D0D0D'
    },
    heroBadge: '',
    heroTagline: "L'audace rencontre la sophistication",
    sectionVisuals: [
      [
        '/assets/moustik/Extras/25.jpg',
        '/assets/moustik/Extras/26.jpg'
      ],
      [
        '/assets/moustik/Extras/texte3.png',
        '/assets/moustik/Extras/texte2.png'
      ]
    ],
    lookbook: { src: '/assets/moustik/moodboard.png', alt: 'Moustik Moodboard' },
    moodboardAfterImage: '/assets/moustik/Extras/dessin.png',
    images: [
      '/assets/date (1).png',
      '/assets/date (2).png',
      '/assets/moustik/date4.png'
    ]
  },
  'les-femmes-du-lavoir': {
    id: "les-femmes-du-lavoir",
    title: "Les femmes du lavoir",
    subtitle: "Costumes de Scène • Histoire & Émancipation",
    heroImage: "/assets/les-femmes-du-lavoir/poster.jpg",
    heroTagline: "1915, quand le linge se tord et les langues se délient, sept femmes racontent la guerre de l’arrière",
    sections: [
      {
        title: "L'histoire",
        content: [
          "Plongée au cœur de l'arrière-front de la Grande Guerre, Les femmes du lavoir de la compagnie Cie C'est pas pire, met en lumière le quotidien de celles qui, dans l'ombre, ont tenu le pays à bout de bras. Le lavoir dépasse sa fonction première pour devenir le théâtre d'une résistance. On y tord le linge autant qu'on y partage l'angoisse des nouvelles du front et une solidarité viscérale.",
          "Pour habiller cette création chorale, j'ai conçu un vestiaire populaire ancré dans la réalité de 1914-1918. Le défi consistait à marier une fidélité historique avec l'ergonomie qu'exigent les gestes amples du lavage sur scène. Le choix s'est naturellement porté sur des textiles bruts et des fibres naturelles, en privilégiant la robustesse du lin et du coton.Travaillées dans une palette chromatique neutre et ces matières ont été patinées pour témoigner de l'usure du temps et de la rudesse de l'effort. À travers ce traitement organique,chaque costume révèle l'histoire singulière d'un personnage tout en composant un tableau collectif cohérent, vibrant hommage à la résilience de ces héroïnes du quotidien."
        ]
      }
    ],
    sectionVisuals: [
      [
        "/assets/les-femmes-du-lavoir/histoire.jpg"
      ]
    ],
    lookbook: {
      src: "/assets/les-femmes-du-lavoir/inspirations.png",
      alt: "Les inspirations - Les femmes du lavoir"
    },
    silhouettes: {
      rows: [
        "/assets/les-femmes-du-lavoir/silhouettes/silhouettes-row1.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouettes-row2.png"
      ],
      items: [
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-01.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-02.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-03.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-04.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-05.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-06.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-07.png",
        "/assets/les-femmes-du-lavoir/silhouettes/silhouette-08.png"
      ]
    },
    images: [
      "/assets/les-femmes-du-lavoir/Galerie/galerie-01.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-02.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-03.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-04.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-05.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-06.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-07.jpg"
    ],
    galleryImages: [
      "/assets/les-femmes-du-lavoir/Galerie/galerie-01.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-02.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-03.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-04.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-05.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-06.jpg",
      "/assets/les-femmes-du-lavoir/Galerie/galerie-07.jpg"
    ],
    theme: {
      primaryColor: "#1A1A1A",
      secondaryColor: "#1A1A1A",
      accentColor: "#F8F5F2",
      backgroundColor: "#F8F5F2",
      frameColor: "#E6DFD5",
      fontFamily: "'Playfair Display', 'Georgia', 'Garamond', serif",
      titleFont: "'Brittany', 'Great Vibes', 'Caveat', cursive"
    }
  }
};

// Alias pour supporter l'URL 'les-lavandieres'
projectsData['les-lavandieres'] = projectsData['les-femmes-du-lavoir'];

// Export pour compatibilité avec anciens imports
export const projectsDetailData = projectsData;